import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Fab,
  Grid,
  Chip,
  Alert,
  Collapse,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  Switch,
  Tabs,
  Tab,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
  Sync as SyncIcon,
  WifiTetheringError as TestConnectionIcon,
  Tune as TuneIcon,
  AccountBalance as OtherIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { Autorenew as RefreshIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { usePlaidLink } from 'react-plaid-link';
import Papa from 'papaparse';

// Mounts Plaid Link once a link_token exists and auto-opens the modal.
const PlaidLinkLauncher = ({ token, onSuccess, onExit }) => {
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });
  useEffect(() => { if (ready) open(); }, [ready, open]);
  return null;
};

// Expandable "how to get a Robinhood token" helper, shared by the
// Add-Connection and Update-Token dialogs.
const RobinhoodTokenHelp = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="small" startIcon={<HelpOutlineIcon fontSize="small" />} onClick={() => setOpen(v => !v)} sx={{ mb: 1 }}>
        How to get this
      </Button>
      <Collapse in={open}>
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
          <b>Easiest — paste the whole auth blob:</b>
          <ol style={{ margin: '4px 0 8px', paddingLeft: 18 }}>
            <li>Log into <b>robinhood.com</b></li>
            <li>Open DevTools (<b>F12</b>) → <b>Console</b> and run:</li>
          </ol>
          <Box component="code" sx={{ display: 'block', p: 1, mb: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.75rem' }}>
            {'copy(localStorage.getItem("web:auth_state"))'}
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            …then paste here — it's copied to your clipboard in one step.
            (Or find it manually: Application → Local Storage → robinhood.com → <code>web:auth_state</code> → copy the value.)
          </Typography>
          <b>Or just the Bearer token:</b> DevTools → <b>Network</b> → any <b>api.robinhood.com</b> request → Request Headers → <b>Authorization</b> → copy everything after <code>Bearer </code>. Token expiry shows automatically once pasted.
        </Alert>
      </Collapse>
    </>
  );
};

// Broker grouping for the account list. Plaid is read-only (non-trading).
const BROKER_SECTIONS = [
  { key: 'schwab', label: 'Schwab', logo: '/assets/schwab_logo.jpg', match: t => t === 'schwab' },
  { key: 'robinhood', label: 'Robinhood', logo: '/assets/robinhood_logo.png', match: t => t === 'robinhood' || t === 'robinhood-api' || t === 'robinhood-mcp' },
  { key: 'tradier', label: 'Tradier', logo: '/assets/tradier-logo.jpg', match: t => t === 'tradier' || t === 'tradier-mcp' },
  { key: 'plaid', label: 'Plaid (non-trading)', logo: '/assets/plaid_logo.svg', match: t => t === 'plaid' },
  { key: 'other', label: 'Other / Manual', logo: null, match: () => true },
];

// api_types that use the REST login credentials (username/password/device token);
// 'robinhood' runs through robin_stocks, 'robinhood-api' through the native JS client.
const isRobinhoodApiType = (t) => t === 'robinhood' || t === 'robinhood-api';

const Accounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [rhLoginStatus, setRhLoginStatus] = useState(null);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncDetailsDialogOpen, setSyncDetailsDialogOpen] = useState(false);
  const [rawSyncData, setRawSyncData] = useState(null);
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  const [plaidRelinkConn, setPlaidRelinkConn] = useState(null); // set when the token is update-mode
  const [pendingConnLabel, setPendingConnLabel] = useState(''); // label from Add-Connection dialog when linking via Plaid
  const [plaidBusy, setPlaidBusy] = useState(false);
  const [rhImportText, setRhImportText] = useState('');
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [moreMenuAccount, setMoreMenuAccount] = useState(null);

  // Form states
  const [newAccountName, setNewAccountName] = useState('');
  const [newApiType, setNewApiType] = useState('manual');
  const [newApiToken, setNewApiToken] = useState('');
  const [newApiAccountId, setNewApiAccountId] = useState('');
  const [newEnvironment, setNewEnvironment] = useState('production');
  const [newSyncEnabled, setNewSyncEnabled] = useState(false);
  const [newSyncFrequency, setNewSyncFrequency] = useState('manual');
  const [newAppKey, setNewAppKey] = useState('');
  const [newAppSecret, setNewAppSecret] = useState('');
  const [newAccountType, setNewAccountType] = useState('taxable');
  // Robinhood (Add)
  const [newRobinUsername, setNewRobinUsername] = useState('');
  const [newRobinPassword, setNewRobinPassword] = useState('');
  const [newRobinDeviceToken, setNewRobinDeviceToken] = useState('');
  const [newRobinMfa, setNewRobinMfa] = useState('');

  // Broker connections (credentials live on the connection; accounts attach)
  const [connections, setConnections] = useState([]);
  const [connDialogOpen, setConnDialogOpen] = useState(false);
  const [connProvider, setConnProvider] = useState('robinhood');
  const [connLabel, setConnLabel] = useState('');
  const [connToken, setConnToken] = useState('');
  const [connAppKey, setConnAppKey] = useState('');
  const [connAppSecret, setConnAppSecret] = useState('');
  const [connBusy, setConnBusy] = useState(false);
  const [connTokenDialog, setConnTokenDialog] = useState(null); // connection being token-updated
  const [connTokenInput, setConnTokenInput] = useState('');
  const [connDeleteTarget, setConnDeleteTarget] = useState(null);
  const [connRenameDialog, setConnRenameDialog] = useState(null);
  const [connRenameInput, setConnRenameInput] = useState('');
  const [topTab, setTopTab] = useState(0);
  const [newConnectionId, setNewConnectionId] = useState('');
  const [editConnectionId, setEditConnectionId] = useState('');

  const [editingAccount, setEditingAccount] = useState(null);
  const [editAccountName, setEditAccountName] = useState('');
  const [editApiType, setEditApiType] = useState('manual');
  const [editApiToken, setEditApiToken] = useState('');
  const [editApiAccountId, setEditApiAccountId] = useState('');
  const [editSubAccounts, setEditSubAccounts] = useState([]);
  const [subAcctsLoading, setSubAcctsLoading] = useState(false);
  const [editEnvironment, setEditEnvironment] = useState('production');
  const [editSyncEnabled, setEditSyncEnabled] = useState(false);
  const [editSyncFrequency, setEditSyncFrequency] = useState('manual');
  const [editAppKey, setEditAppKey] = useState('');
  const [editAppSecret, setEditAppSecret] = useState('');
  const [editAccountType, setEditAccountType] = useState('taxable');
  // Robinhood (Edit)
  const [editRobinUsername, setEditRobinUsername] = useState('');
  const [editRobinPassword, setEditRobinPassword] = useState('');
  const [editRobinDeviceToken, setEditRobinDeviceToken] = useState('');
  const [editRobinMfa, setEditRobinMfa] = useState('');
  const [oauthRedirectUri, setOauthRedirectUri] = useState('');
  const [oauthAuthUrl, setOauthAuthUrl] = useState('');
  const [oauthCode, setOauthCode] = useState('');
  const [oauthLoading, setOauthLoading] = useState(false);

  const [deletingAccount, setDeletingAccount] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [syncingAccount, setSyncingAccount] = useState(null);
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [syncProgress, setSyncProgress] = useState('');
  const [syncElapsedTime, setSyncElapsedTime] = useState(0);

  // Import states
  const [file, setFile] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('schwab');
  const [importHistory, setImportHistory] = useState([]);
  const [previewData, setPreviewData] = useState([]);

  useEffect(() => {
    fetchAccounts();
    fetchConnections();
  }, []);

  // Handle return from the Schwab OAuth callback (?schwab_auth=success|error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('schwab_auth');
    if (!status) return;
    if (status === 'success') {
      setSuccess('Schwab authorized — tokens saved.');
      fetchAccounts();
      fetchConnections();
    } else {
      const reason = params.get('reason');
      setError(`Schwab authorization failed${reason ? `: ${reason.replace(/_/g, ' ')}` : ''}`);
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/accounts?include_inactive=true');
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await apiRequest('/api/broker-connections');
      setConnections(response.data || []);
    } catch (e) {
      // Connections API is additive — don't break the page if unavailable
      console.warn('Failed to fetch broker connections:', e.message);
    }
  };

  // api_type → matching connection provider
  const connProviderForType = (t) => {
    const type = String(t || '').toLowerCase();
    if (type === 'robinhood' || type === 'robinhood-api') return ['robinhood', 'robinhood-api'];
    if (type === 'robinhood-mcp') return ['robinhood-mcp'];
    if (type === 'tradier-mcp') return ['tradier-mcp'];
    return [type];
  };
  const connectionsForType = (t) => {
    const p = connProviderForType(t);
    return connections.filter(c => p.includes(c.provider));
  };

  // Accept a bare JWT, "Bearer <jwt>", or a JSON session blob.
  const parseTokenInput = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return { access_token: null };
    if (s.startsWith('{')) {
      try {
        const o = JSON.parse(s);
        return { access_token: o.access_token || null, refresh_token: o.refresh_token || null, device_token: o.device_token || null };
      } catch { return { access_token: null }; }
    }
    return { access_token: s.replace(/^bearer\s+/i, '') };
  };

  const decodeJwtExp = (tok) => {
    try {
      const part = String(tok || '').split('.')[1];
      if (!part) return null;
      const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
      return payload.exp ? new Date(payload.exp * 1000) : null;
    } catch { return null; }
  };

  const handleCreateConnection = async () => {
    if (!connLabel.trim()) { setError('Connection label is required'); return; }
    const parsed = parseTokenInput(connToken);
    const isSchwab = connProvider === 'schwab';
    if (!isSchwab && !parsed.access_token) { setError('A valid access token / API key is required'); return; }
    if (isSchwab && (!connAppKey.trim() || !connAppSecret.trim())) { setError('App Key and App Secret are required for Schwab'); return; }
    try {
      setConnBusy(true);
      const resp = await apiRequest('/api/broker-connections', {
        method: 'POST',
        body: JSON.stringify({
          provider: connProvider,
          label: connLabel.trim(),
          ...(parsed.access_token ? { access_token: parsed.access_token } : {}),
          ...(parsed.refresh_token ? { refresh_token: parsed.refresh_token } : {}),
          ...(parsed.device_token ? { device_token: parsed.device_token } : {}),
          ...(isSchwab ? { app_key: connAppKey.trim(), app_secret: connAppSecret.trim() } : {}),
        }),
      });
      const n = (resp.data?.accountNumbers || []).length;
      setSuccess(`Connection "${connLabel.trim()}" created${n ? ` — ${n} broker account(s) found` : ''}`);
      setConnDialogOpen(false);
      setConnLabel(''); setConnToken(''); setConnAppKey(''); setConnAppSecret('');
      fetchConnections();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create connection');
    } finally {
      setConnBusy(false);
    }
  };

  const handleUpdateConnectionToken = async () => {
    const parsed = parseTokenInput(connTokenInput);
    if (!parsed.access_token) { setError('Paste a valid bearer token (or JSON session blob)'); return; }
    try {
      setConnBusy(true);
      const resp = await apiRequest(`/api/broker-connections/${connTokenDialog.id}/token`, {
        method: 'POST',
        body: JSON.stringify({
          access_token: parsed.access_token,
          ...(parsed.refresh_token ? { refresh_token: parsed.refresh_token } : {}),
          ...(parsed.device_token ? { device_token: parsed.device_token } : {}),
        }),
      });
      const exp = resp.data?.connection?.tokenExpiresAt;
      setSuccess(`Token updated for "${connTokenDialog.label}"${exp ? ` — expires ${new Date(exp).toLocaleString()}` : ''}`);
      setConnTokenDialog(null);
      setConnTokenInput('');
      fetchConnections();
      fetchAccounts();
    } catch (e) {
      setError(e.response?.data?.error || 'Token update failed');
    } finally {
      setConnBusy(false);
    }
  };

  const handleSchwabAuthorize = async (conn, accountId) => {
    try {
      setConnBusy(true);
      const resp = await apiRequest(`/api/schwab-api/oauth/authorize?accountId=${accountId}`);
      const url = resp.data?.authorizeUrl;
      if (!url) { setError('Failed to generate Schwab authorization URL'); return; }
      window.open(url, '_blank', 'width=960,height=760');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start Schwab authorization');
    } finally {
      setConnBusy(false);
    }
  };

  const handleTestConnectionRow = async (conn) => {
    try {
      setConnBusy(true);
      const resp = await apiRequest(`/api/broker-connections/${conn.id}/test`, { method: 'POST' });
      if (resp.data?.success) setSuccess(`"${conn.label}" token is valid`);
      else setError(`"${conn.label}": ${resp.data?.error || 'test failed'}`);
      fetchConnections();
    } catch (e) {
      setError(e.response?.data?.error || 'Test failed');
    } finally {
      setConnBusy(false);
    }
  };

  const handleDiscover = async (conn) => {
    try {
      setConnBusy(true);
      const resp = await apiRequest(`/api/broker-connections/${conn.id}/discover`, { method: 'POST' });
      const d = resp.data || {};
      setSuccess(`"${conn.label}": ${d.found || 0} broker account(s) found — ${d.created || 0} created, ${d.attached || 0} attached`);
      fetchConnections();
      fetchAccounts();
    } catch (e) {
      setError(e.response?.data?.error || 'Discovery failed');
    } finally {
      setConnBusy(false);
    }
  };

  const handleRenameConnection = async () => {
    const label = connRenameInput.trim();
    if (!label) { setError('Label is required'); return; }
    try {
      setConnBusy(true);
      await apiRequest(`/api/broker-connections/${connRenameDialog.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ label }),
      });
      setSuccess(`Connection renamed to "${label}"`);
      setConnRenameDialog(null);
      fetchConnections();
    } catch (e) {
      setError(e.response?.data?.error || 'Rename failed');
    } finally {
      setConnBusy(false);
    }
  };

  const handleDeleteConnection = async () => {
    try {
      setConnBusy(true);
      await apiRequest(`/api/broker-connections/${connDeleteTarget.id}`, { method: 'DELETE' });
      setSuccess(`Connection "${connDeleteTarget.label}" deleted`);
      setConnDeleteTarget(null);
      fetchConnections();
    } catch (e) {
      setError(e.response?.data?.error || 'Delete failed');
    } finally {
      setConnBusy(false);
    }
  };

  // Plaid Link flow — fetch a link token, mount the launcher, then exchange
  // the public token which creates one account row per Plaid sub-account.
  const handleConnectPlaid = async () => {
    setPlaidBusy(true);
    setError(null);
    try {
      const resp = await apiRequest('/api/plaid-api/link-token', { method: 'POST', body: '{}' });
      if (resp.data?.link_token) {
        setPlaidLinkToken(resp.data.link_token);
      } else {
        setError(resp.data?.error || 'Failed to create Plaid link token — check Plaid credentials in Profile settings');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to create Plaid link token');
    } finally {
      setPlaidBusy(false);
    }
  };

  const handlePlaidRelink = async (conn) => {
    setConnBusy(true);
    setError(null);
    try {
      const resp = await apiRequest('/api/plaid-api/link-token', {
        method: 'POST',
        body: JSON.stringify({ connectionId: conn.id }),
      });
      if (resp.data?.link_token) {
        setPlaidRelinkConn(conn);
        setPlaidLinkToken(resp.data.link_token);
      } else {
        setError(resp.data?.error || 'Failed to create Plaid update link token');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to create Plaid link token');
    } finally {
      setConnBusy(false);
    }
  };

  // Update-mode Link completed — the item's access_token is unchanged; just
  // re-validate the connection and refresh.
  const handlePlaidRelinkSuccess = async () => {
    const conn = plaidRelinkConn;
    setPlaidLinkToken(null);
    setPlaidRelinkConn(null);
    try {
      await apiRequest(`/api/broker-connections/${conn.id}/test`, { method: 'POST' });
      setSuccess(`"${conn.label}" re-authenticated`);
    } catch {
      setSuccess(`"${conn.label}" re-linked`);
    }
    fetchConnections();
  };

  const handlePlaidSuccess = async (publicToken) => {
    setPlaidLinkToken(null);
    try {
      const resp = await apiRequest('/api/plaid-api/exchange', {
        method: 'POST',
        body: JSON.stringify({ publicToken, connectionLabel: pendingConnLabel || undefined }),
      });
      setPendingConnLabel('');
      if (resp.data?.success) {
        const names = (resp.data.accounts || []).map(a => a.name).join(', ');
        setSuccess(`Linked ${resp.data.institution}: ${names}`);
        fetchAccounts();
        fetchConnections();
      } else {
        setError(resp.data?.error || 'Plaid link failed');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Plaid link failed');
    }
  };

  const handleToggleActive = async (account) => {
    try {
      const newActive = !account.isActive;
      // Optimistic update
      setAccounts(prev => prev.map(a =>
        a.id === account.id ? { ...a, isActive: newActive } : a
      ));

      await apiRequest(`/api/accounts/${account.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: newActive })
      });

      setSuccess(`Account ${account.name} ${newActive ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating account status:', error);
      setError('Failed to update account status');
      // Revert on error
      fetchAccounts();
    }
  };

  // Designate one trading account as the Quick Trade default. The server
  // clears is_default on the user's other accounts when set.
  const handleSetDefault = async (account) => {
    const newVal = !account.isDefault;
    try {
      setAccounts(prev => prev.map(a =>
        a.id === account.id ? { ...a, isDefault: newVal } : (newVal ? { ...a, isDefault: false } : a)
      ));
      await apiRequest(`/api/accounts/${account.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isDefault: newVal })
      });
      setSuccess(newVal ? `"${account.name}" set as default trading account` : `"${account.name}" removed as default`);
    } catch (error) {
      console.error('Error setting default account:', error);
      setError('Failed to update default account');
      fetchAccounts();
    }
  };

  const handleAddAccount = async () => {
    if (!newAccountName.trim()) {
      setError('Account name is required');
      return;
    }

    // Validate API credentials based on provider
    if (newApiType === 'tradier') {
      if (!newConnectionId && !newApiToken.trim()) {
        setError('API Token is required for Tradier (or pick a Connection)');
        return;
      }
      if (!newApiAccountId.trim()) {
        setError('API Account ID is required for Tradier');
        return;
      }
    } else if (newApiType === 'schwab') {
      if (!newConnectionId && (!newAppKey.trim() || !newAppSecret.trim())) {
        setError('App Key and App Secret are required for Schwab (or pick a Connection)');
        return;
      }
    } else if (isRobinhoodApiType(newApiType)) {
      if (!newConnectionId && (!newRobinUsername.trim() || !newRobinPassword.trim())) {
        setError('Robinhood username and password are required (or pick a Connection)');
        return;
      }
    } else if (newApiType === 'robinhood-mcp') {
      if (!newApiAccountId.trim()) {
        setError('Account number is required for Robinhood MCP');
        return;
      }
    }

    try {
      setLoading(true);
      const accountData = {
        name: newAccountName.trim(),
        apiType: newApiType,
        accountType: newAccountType,
        // Inline credential payloads are skipped when a connection is attached
        apiToken: newConnectionId ? undefined : (newApiType === 'tradier' ? newApiToken.trim() : (isRobinhoodApiType(newApiType) ? JSON.stringify({
          username: newRobinUsername.trim(),
          password: newRobinPassword,
          device_token: newRobinDeviceToken.trim() || null,
          mfa: newRobinMfa.trim() || null
        }) : null)),
        apiAccountId: (newApiType === 'tradier' || newApiType === 'robinhood-mcp' || isRobinhoodApiType(newApiType)) ? newApiAccountId.trim() : null,
        environment: newEnvironment,
        syncEnabled: newSyncEnabled,
        syncFrequency: newSyncFrequency,
        appKey: newApiType === 'schwab' && !newConnectionId ? newAppKey.trim() : undefined,
        appSecret: newApiType === 'schwab' && !newConnectionId ? newAppSecret.trim() : undefined,
        connectionId: newConnectionId || undefined,
      };

      const resp = await apiRequest('/api/accounts', {
        method: 'POST',
        body: JSON.stringify(accountData)
      });
      const created = resp.data?.account;
      if (isRobinhoodApiType(newApiType) && created?.id && !newConnectionId) {
        setSuccess('Account created — signing in to Robinhood in the background…');
        watchRobinhoodProvision(created);
      } else {
        setSuccess('Account created successfully');
      }

      // Reset form
      setNewAccountName('');
      setNewApiType('manual');
      setNewApiToken('');
      setNewApiAccountId('');
      setNewEnvironment('production');
      setNewSyncEnabled(false);
      setNewSyncFrequency('manual');
      setNewAppKey('');
      setNewAppSecret('');
      setNewAccountType('taxable');
      setNewRobinUsername('');
      setNewRobinPassword('');
      setNewRobinDeviceToken('');
      setNewRobinMfa('');
      setNewConnectionId('');

      setAddDialogOpen(false);
      fetchAccounts();
    } catch (error) {
      console.error('Error creating account:', error);
      setError(error.response?.data?.error || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAccount = async () => {
    if (!editAccountName.trim()) {
      setError('Account name is required');
      return;
    }

    // Validate API credentials based on provider
    if (editApiType === 'tradier') {
      if (!editConnectionId && !editApiToken.trim()) {
        setError('API Token is required for Tradier (or pick a Connection)');
        return;
      }
      if (!editApiAccountId.trim()) {
        setError('API Account ID is required for Tradier');
        return;
      }
    } else if (editApiType === 'schwab') {
      // App Key/Secret optional on update; only validate if provided
      if (!editConnectionId && ((editAppKey && !editAppKey.trim()) || (editAppSecret && !editAppSecret.trim()))) {
        setError('Provided App Key/Secret must be non-empty');
        return;
      }
    } else if (isRobinhoodApiType(editApiType)) {
      // Optional: only validate if any field provided, require username+password together
      const anyProvided = !editConnectionId && !!(editRobinUsername || editRobinPassword || editRobinDeviceToken || editRobinMfa);
      if (anyProvided && (!editRobinUsername.trim() || !editRobinPassword.trim())) {
        setError('Provide both username and password when updating Robinhood credentials');
        return;
      }
    } else if (editApiType === 'robinhood-mcp') {
      if (!editApiAccountId.trim()) {
        setError('Account number is required for Robinhood MCP');
        return;
      }
    }

    try {
      setLoading(true);
      const accountData = {
        name: editAccountName.trim(),
        apiType: editApiType,
        accountType: editAccountType,
        // Only include apiToken for tradier or when updating robinhood creds
        ...(editApiType === 'tradier' ? { apiToken: editApiToken.trim() } : {}),
        apiAccountId: (editApiType === 'tradier' || editApiType === 'robinhood-mcp' || isRobinhoodApiType(editApiType)) ? editApiAccountId.trim() : null,
        environment: editEnvironment,
        syncEnabled: editSyncEnabled,
        syncFrequency: editSyncFrequency,
        // Connection attach/detach — send when the picker value differs from current
        ...(editConnectionId !== (editingAccount.connectionId || '') ? { connectionId: editConnectionId || null } : {}),
        // Only include appKey/appSecret when editing Schwab and provided
        ...(editApiType === 'schwab' && editAppKey ? { appKey: editAppKey.trim() } : {}),
        ...(editApiType === 'schwab' && editAppSecret ? { appSecret: editAppSecret.trim() } : {}),
        // Only include apiToken for robinhood if user provided new credentials
        ...(isRobinhoodApiType(editApiType) && (editRobinUsername || editRobinPassword || editRobinDeviceToken || editRobinMfa) ? {
          apiToken: JSON.stringify({
            username: editRobinUsername.trim(),
            password: editRobinPassword,
            device_token: editRobinDeviceToken.trim() || null,
            mfa: editRobinMfa.trim() || null
          })
        } : {})
      };

      await apiRequest(`/api/accounts/${editingAccount.id}`, {
        method: 'PUT',
        body: JSON.stringify(accountData)
      });
      setSuccess('Account updated successfully');
      setEditDialogOpen(false);
      setEditingAccount(null);

      // Reset form
      setEditAccountName('');
      setEditApiType('manual');
      setEditApiToken('');
      setEditApiAccountId('');
      setEditEnvironment('production');
      setEditSyncEnabled(false);
      setEditSyncFrequency('manual');
      setEditAppKey('');
      setEditAppSecret('');
      setEditAccountType('taxable');
      setEditRobinUsername('');
      setEditRobinPassword('');
      setEditRobinDeviceToken('');
      setEditRobinMfa('');
      setOauthRedirectUri('');
      setOauthAuthUrl('');
      setOauthCode('');

      fetchAccounts();
    } catch (error) {
      console.error('Error updating account:', error);
      setError(error.response?.data?.error || 'Failed to update account');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      await apiRequest(`/api/accounts/${deletingAccount.id}`, {
        method: 'DELETE'
      });
      setSuccess('Account and all associated data deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      setError(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAccountData = async () => {
    try {
      setLoading(true);
      await apiRequest(`/api/accounts/${deletingAccount.id}/clear-data`, {
        method: 'DELETE'
      });
      setSuccess('All trade data cleared successfully. Account remains active.');
      setDeleteDialogOpen(false);
      setDeletingAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error('Error clearing account data:', error);
      setError(error.response?.data?.error || 'Failed to clear account data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);

      if (selectedFormat === 'pershing-holdings' || selectedFormat === 'pershing-history') {
        // Excel files are not previewed
        setPreviewData([]);
        return;
      }
      if (selectedFormat === 'pershing-transactions') {
        // CSV file, no preview needed
        setPreviewData([]);
        return;
      }

      // Preview the file
      Papa.parse(selectedFile, {
        header: true,
        preview: 5,
        complete: (results) => {
          setPreviewData(results.data);
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
          setError('Error parsing CSV file');
        }
      });
    }
  };

  const handleImport = async () => {
    if (!file || !selectedAccount) {
      setError('Please select a file and account');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('format', selectedFormat);
      formData.append('account', selectedAccount.name);

      let endpoint = '/api/imports/upload-tradier';
      if (selectedFormat === 'schwab') {
        endpoint = '/api/imports/upload-schwab';
      } else if (selectedFormat === 'robinhood') {
        endpoint = '/api/imports/upload-robinhood';
      } else if (selectedFormat === 'pershing-holdings') {
        endpoint = '/api/imports/upload-pershing-holdings';
      } else if (selectedFormat === 'pershing-history') {
        endpoint = '/api/imports/upload-pershing-history';
      } else if (selectedFormat === 'pershing-transactions') {
        endpoint = '/api/imports/upload-pershing-transactions';
      }
      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: formData
      });

      setSuccess(`Successfully imported ${response.data.processedCount} trades${response.data.duplicateCount > 0 ? ` (${response.data.duplicateCount} duplicates skipped)` : ''}`);
      setFile(null);
      setPreviewData([]);
      setImportDialogOpen(false);

      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Import error:', error);
      setError(error.response?.data?.error || 'Failed to import data');
    } finally {
      setLoading(false);
    }
  };

  const fetchImportHistory = async (account) => {
    try {
      setLoading(true);
      setSelectedAccount(account);
      const response = await apiRequest(`/api/imports/history?account=${account.name}`);
      setImportHistory(response.data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error fetching import history:', error);
      setError('Failed to fetch import history');
      setImportHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAccount = (account) => {
    setSyncingAccount(account);

    // Preload start date with last sync date if available
    if (account.lastSyncDate) {
      const lastSync = new Date(account.lastSyncDate);
      // Start from the day after last sync to avoid duplicates
      lastSync.setDate(lastSync.getDate() + 1);
      setSyncStartDate(lastSync.toISOString().split('T')[0]);
    } else {
      setSyncStartDate('');
    }

    setSyncEndDate(new Date().toISOString().split('T')[0]);
    setSyncDialogOpen(true);
  };

  const handleReconcileTrades = async (account) => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('user_id');
      const response = await apiRequest('/api/sync/reconcile', {
        method: 'POST',
        body: JSON.stringify({ userId, accountId: account.id })
      });

      if (response.data?.success) {
        const results = response.data.results;
        const totalUpdated = results.reduce((sum, r) => sum + (r.updated || 0), 0);
        setSuccess(`Reconciliation completed for ${results.length} account(s). ${totalUpdated} assignment trades updated to P/L = 0.`);
      } else {
        setError('Reconciliation failed');
      }
    } catch (error) {
      console.error('Error reconciling trades:', error);
      setError(error.response?.data?.error || 'Failed to reconcile trades');
    } finally {
      setLoading(false);
    }
  };

  const performSync = async () => {
    let timerInterval;
    try {
      setLoading(true);
      setSyncProgress('Connecting to API...');
      setSyncElapsedTime(0);

      // Start timer
      timerInterval = setInterval(() => {
        setSyncElapsedTime(prev => prev + 1);
      }, 1000);

      // Simulated progress steps based on elapsed time
      const progressSteps = [
        { time: 2, message: 'Fetching transaction data...' },
        { time: 5, message: 'Processing trades...' },
        { time: 10, message: 'Processing dividends...' },
        { time: 15, message: 'Applying FIFO matching...' },
        { time: 20, message: 'Calculating realized P/L...' },
        { time: 30, message: 'Finalizing sync...' },
      ];

      const progressTimer = setInterval(() => {
        const elapsed = syncElapsedTime;
        const step = progressSteps.find(s => elapsed >= s.time);
        if (step) {
          setSyncProgress(step.message);
        }
      }, 1000);

      const syncData = {};
      if (syncStartDate) syncData.startDate = syncStartDate;
      if (syncEndDate) syncData.endDate = syncEndDate;

      const response = await apiRequest(`/api/accounts/${syncingAccount.id}/sync`, {
        method: 'POST',
        body: JSON.stringify(syncData)
      });

      clearInterval(timerInterval);
      clearInterval(progressTimer);

      const importedCount = response.data.importedCount || 0;
      const duplicateCount = response.data.duplicateCount || 0;

      setSyncProgress(`Completed: ${importedCount} new trades, ${duplicateCount} duplicates`);

      setSuccess(`Sync completed for ${syncingAccount.name}: ${importedCount} new trades, ${duplicateCount} duplicates`);

      // Store raw data for viewing if available
      if (response.data.rawResponseSample) {
        setRawSyncData(response.data);
        setSyncDetailsDialogOpen(true);
      }

      fetchAccounts(); // Refresh the accounts list
      setSyncDialogOpen(false);
    } catch (error) {
      console.error('Error syncing account:', error);
      setSyncProgress('Error during sync');
      setError(error.response?.data?.error || 'Failed to sync account data');
    } finally {
      if (timerInterval) clearInterval(timerInterval);
      setLoading(false);
      setTimeout(() => {
        setSyncProgress('');
        setSyncElapsedTime(0);
      }, 3000);
    }
  };

  // While a Robinhood request is in flight, poll the backend's login-status
  // file so a login blocked on device approval shows a live hint instead of
  // silently waiting for the subprocess timeout.
  const startRhStatusPoll = (account) => {
    const timer = setInterval(async () => {
      try {
        const st = await apiRequest(`/api/robinhood-api/login-status/${account.id}`);
        const s = st.data?.state;
        if (s === 'device_approval') {
          setRhLoginStatus(`Robinhood is waiting for approval — open the Robinhood app on ${account.name}'s device and approve the sign-in request.`);
        } else if (s === 'needs_code') {
          setRhLoginStatus(`Robinhood sent a verification code — have it ready; you'll be prompted for it.`);
        }
      } catch { /* status poll is best-effort */ }
    }, 3000);
    return () => { clearInterval(timer); setRhLoginStatus(null); };
  };

  // After Add Account, the server logs into Robinhood in the background and
  // splits multi-account logins into per-sub-account rows. Poll login-status
  // so the user sees device-approval prompts and the terminal result without
  // having to press Test Connection.
  const watchRobinhoodProvision = (account) => {
    const timer = setInterval(async () => {
      try {
        const st = await apiRequest(`/api/robinhood-api/login-status/${account.id}`);
        const s = st.data?.state;
        if (s === 'device_approval') {
          setRhLoginStatus(`Robinhood is waiting for approval — open the Robinhood app on ${account.name}'s device and approve the sign-in request.`);
        } else if (s === 'needs_code') {
          setRhLoginStatus(`Robinhood sent a verification code — use Test Connection on the new account to enter it.`);
        } else if (s === 'done') {
          clearInterval(timer);
          setRhLoginStatus(null);
          fetchAccounts();
          const n = st.data?.count || 0;
          setSuccess(n > 1
            ? `Robinhood sign-in complete — ${n} sub-accounts found; separate accounts were created.`
            : 'Robinhood sign-in complete.');
        } else if (s === 'failed') {
          clearInterval(timer);
          setRhLoginStatus(null);
          setError(`Robinhood sign-in failed: ${st.data?.detail || 'use Test Connection on the account to retry'}`);
        }
      } catch { /* status poll is best-effort */ }
    }, 3000);
    // Stop watching after ~4 minutes; refresh the list with whatever landed.
    setTimeout(() => { clearInterval(timer); setRhLoginStatus(null); fetchAccounts(); }, 240000);
  };

  const handleTestConnection = async (account) => {
    let stopRhPoll = null;
    try {
      setLoading(true);
      let response;
      const type = String(account.apiType).toLowerCase();
      if (isRobinhoodApiType(type)) {
        stopRhPoll = startRhStatusPoll(account);
        response = await apiRequest(`/api/robinhood-api/test-connection/${account.id}`, {
          method: 'GET'
        });
        if (!response.data?.success) {
          const hint = response.data?.hint || '';
          const errText = `${response.data?.error || ''} ${response.data?.details || ''}`.toLowerCase();
          const looksLikeCodeChallenge = hint === 'code' || (/\bmfa\b|\botp\b|verification code|sms|email/.test(errText) && !/device|approv|workflow/.test(errText));
          const looksLikeDeviceApproval = hint === 'device_approval' || /device approv|verification|workflow|timed out/.test(errText);
          if (looksLikeCodeChallenge) {
            const code = window.prompt('Enter the Robinhood verification code:');
            if (code && code.trim()) {
              response = await apiRequest(`/api/robinhood-api/test-connection/${account.id}?mfa=${encodeURIComponent(code.trim())}`, {
                method: 'GET'
              });
            }
          } else if (looksLikeDeviceApproval) {
            // Robinhood uses in-app device approval, not an MFA code
            const retry = window.confirm(
              'Robinhood login needs approval in your Robinhood app (device approval).\n\n' +
              'Approve the sign-in request in the app, then click OK to retry. Click Cancel to see the error details.'
            );
            if (retry) {
              response = await apiRequest(`/api/robinhood-api/test-connection/${account.id}`, {
                method: 'GET'
              });
            }
          }
        }
      } else {
        response = await apiRequest(`/api/accounts/${account.id}/test-connection`, {
          method: 'POST'
        });
      }
      const ok = !!response.data?.success;
      if (ok) {
        const prov = response.data?.provisioned;
        let msg = `Connection test successful for ${account.name}`;
        if (prov?.count > 1) {
          msg += prov.created?.length
            ? ` — ${prov.count} Robinhood sub-accounts found; created ${prov.created.length} additional account(s)`
            : ` — ${prov.count} Robinhood sub-accounts found; account pinned to ${prov.alreadyPinned ? 'its configured number' : 'the first sub-account'}`;
          fetchAccounts();
        }
        setSuccess(msg);
      } else {
        const errMsg = [response.data?.error, response.data?.details].filter(Boolean).join(' — ');
        setError(`Connection test failed for ${account.name}: ${errMsg || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setError(error.response?.data?.error || 'Failed to test API connection');
    } finally {
      if (stopRhPoll) stopRhPoll();
      setLoading(false);
    }
  };

  // Paste a session blob produced by `node server/scripts/robinhood_login.js`
  // (run from a non-datacenter IP) when RH rate-limits server-side logins.
  const handleImportSession = async () => {
    const account = editingAccount;
    let blob;
    try {
      blob = JSON.parse(rhImportText);
    } catch {
      setError('Invalid JSON — paste the exact output of robinhood_login.js');
      return;
    }
    try {
      setLoading(true);
      setRhImportText('');
      const resp = account.connectionId
        ? await apiRequest(`/api/broker-connections/${account.connectionId}/token`, {
            method: 'POST',
            body: JSON.stringify({
              access_token: blob.access_token,
              refresh_token: blob.refresh_token,
              device_token: blob.device_token,
            }),
          })
        : await apiRequest(`/api/robinhood-api/import-session/${account.id}`, {
            method: 'POST',
            body: JSON.stringify(blob),
          });
      if (resp.data?.success) {
        const prov = resp.data?.provisioned;
        setSuccess(
          `Session imported for ${account.name}` +
          (prov?.count > 1 ? ` — ${prov.count} sub-accounts found` : '')
        );
        fetchAccounts();
      } else {
        setError(`Session import failed for ${account.name}: ${resp.data?.error || 'Unknown error'}`);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to import session');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async (account) => {
    try {
      setLoading(true);
      const resp = await apiRequest('/api/schwab-api/refresh-token', {
        method: 'POST',
        body: JSON.stringify({ accountId: account.id })
      });
      if (resp.data?.success) {
        setSuccess('Access token refreshed successfully');
        fetchAccounts();
      } else {
        setError(resp.data?.error || 'Failed to refresh token');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      setError(error.response?.data?.error || 'Failed to refresh token');
    } finally {
      setLoading(false);
    }
  };

  const handleGetRobinhoodPositions = async (account) => {
    let stopRhPoll = null;
    try {
      // Skip disabled accounts
      if (!account.isActive) {
        setError('Account is disabled. Enable it to fetch positions.');
        return;
      }
      setLoading(true);
      stopRhPoll = startRhStatusPoll(account);
      const resp = await apiRequest(`/api/robinhood-api/positions/${account.id}`);
      // Support both token-based response and python-service response shapes
      const results = resp.data?.results || [];
      if (Array.isArray(results) && results.length > 0) {
        const totalEq = results.reduce((sum, r) => sum + (Array.isArray(r.equityPositions) ? r.equityPositions.length : 0), 0);
        const totalOpt = results.reduce((sum, r) => sum + (Array.isArray(r.optionPositions) ? r.optionPositions.length : 0), 0);
        setSuccess(`Robinhood positions loaded: ${totalEq} equities, ${totalOpt} options across ${results.length} account(s)`);
        console.log('Robinhood positions (token response):', results);
      } else if (resp.data?.positions) {
        // Python-based service may return a different structure
        const positions = resp.data.positions;
        const eqCount = Array.isArray(positions?.equityPositions) ? positions.equityPositions.length : 0;
        const optCount = Array.isArray(positions?.optionPositions) ? positions.optionPositions.length : 0;
        setSuccess(`Robinhood positions loaded: ${eqCount} equities, ${optCount} options`);
        console.log('Robinhood positions (python response):', positions);
      } else {
        setSuccess('Robinhood positions loaded (no positions reported)');
        console.log('Robinhood positions response:', resp.data);
      }
    } catch (error) {
      console.error('Error fetching Robinhood positions:', error);
      setError(error.response?.data?.error || 'Failed to fetch Robinhood positions');
    } finally {
      if (stopRhPoll) stopRhPoll();
      setLoading(false);
    }
  };

  const discoverRobinhoodAccounts = async () => {
    if (!editingAccount) return;
    let stopRhPoll = null;
    try {
      setSubAcctsLoading(true);
      setError('');
      stopRhPoll = startRhStatusPoll(editingAccount);
      const resp = await apiRequest(`/api/robinhood-api/subaccounts/${editingAccount.id}`);
      const list = resp.data?.accounts || [];
      setEditSubAccounts(list);
      if (!list.length) setError('No Robinhood sub-accounts found under this login');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch Robinhood sub-accounts');
    } finally {
      if (stopRhPoll) stopRhPoll();
      setSubAcctsLoading(false);
    }
  };

  const handleGetTradierMcpPositions = async (account) => {
    try {
      // Skip disabled accounts
      if (!account.isActive) {
        setError('Account is disabled. Enable it to fetch positions.');
        return;
      }
      setLoading(true);
      const resp = await apiRequest(`/api/mcp/tradier/positions?accountId=${account.id}`);
      const positions = resp.data?.positions || [];
      const totalEq = positions.filter(p => !p.isOption).length;
      const totalOpt = positions.filter(p => p.isOption).length;
      setSuccess(`Tradier MCP positions loaded: ${totalEq} equities, ${totalOpt} options`);
      console.log('Tradier MCP positions:', positions);
    } catch (error) {
      console.error('Error fetching Tradier MCP positions:', error);
      setError(error.response?.data?.error || 'Failed to fetch Tradier MCP positions');
    } finally {
      setLoading(false);
    }
  };

  const openMoreMenu = (e, account) => { setMoreMenuAnchor(e.currentTarget); setMoreMenuAccount(account); };
  const closeMoreMenu = () => { setMoreMenuAnchor(null); setMoreMenuAccount(null); };
  const runMoreAction = (fn) => { const acct = moreMenuAccount; closeMoreMenu(); if (acct) fn(acct); };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setEditAccountName(account.name);
    setEditApiType(account.apiType || 'manual');
    setEditApiToken(account.apiToken || '');
    setEditApiAccountId(account.apiAccountId || '');
    setEditSubAccounts([]);
    setEditEnvironment(account.environment || 'production');
    setEditSyncEnabled(account.syncEnabled || false);
    setEditSyncFrequency(account.syncFrequency || 'manual');
    setEditAccountType(account.accountType || 'taxable');
    setEditConnectionId(account.connectionId || '');
    setEditAppKey('');
    setEditAppSecret('');
    setRhImportText('');
    setOauthRedirectUri('https://mytradeshub.net/api/schwab-api/oauth/callback');
    setOauthAuthUrl('');
    setOauthCode('');
    setEditDialogOpen(true);
  };

  const handleSchwabConnect = async () => {
    if (!editingAccount) return;
    try {
      setOauthLoading(true);
      const resp = await apiRequest(`/api/schwab-api/oauth/authorize?accountId=${editingAccount.id}`);
      const url = resp.data?.authorizeUrl;
      if (!url) {
        setError('Failed to generate Schwab authorization URL');
        return;
      }
      window.location.href = url;
    } catch (err) {
      console.error('Schwab connect error:', err);
      setError(err.response?.data?.error || 'Failed to start Schwab authorization');
      setOauthLoading(false);
    }
  };

  const generateSchwabAuthUrl = async () => {
    if (!editingAccount) return;
    if (!oauthRedirectUri.trim()) {
      setError('Redirect URI is required to generate OAuth URL');
      return;
    }
    try {
      setOauthLoading(true);
      const resp = await apiRequest(`/api/schwab-api/oauth/auth-url?accountId=${editingAccount.id}&redirectUri=${encodeURIComponent(oauthRedirectUri.trim())}`);
      const url = resp.data?.authorizeUrl;
      if (!url) {
        setError('Failed to generate OAuth URL');
        return;
      }
      setOauthAuthUrl(url);
      window.open(url, '_blank');
      setSuccess('OAuth URL generated. Complete auth in new tab.');
    } catch (err) {
      console.error('Generate OAuth URL error:', err);
      setError(err.response?.data?.error || 'Failed to generate OAuth URL');
    } finally {
      setOauthLoading(false);
    }
  };

  const exchangeSchwabCode = async () => {
    if (!editingAccount) return;
    if (!oauthCode.trim() || !oauthRedirectUri.trim()) {
      setError('Authorization code and Redirect URI are required');
      return;
    }
    try {
      setOauthLoading(true);
      const body = {
        accountId: editingAccount.id,
        code: oauthCode.trim(),
        redirectUri: oauthRedirectUri.trim()
      };
      const resp = await apiRequest('/api/schwab-api/oauth/exchange', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (resp.data?.success) {
        setSuccess('OAuth code exchanged. Access token saved.');
        fetchAccounts();
      } else {
        setError('Failed to exchange OAuth code');
      }
    } catch (err) {
      console.error('Exchange OAuth code error:', err);
      setError(err.response?.data?.error || 'Failed to exchange OAuth code');
    } finally {
      setOauthLoading(false);
    }
  };

  const startMcpAuth = async () => {
    try {
      setOauthLoading(true);
      const resp = await apiRequest('/api/mcp/auth/start');
      if (resp.data?.authorized) {
        setSuccess('MCP already authorized');
      } else if (resp.data?.authorizationUrl) {
        setOauthAuthUrl(resp.data.authorizationUrl);
        window.open(resp.data.authorizationUrl, '_blank');
        setSuccess('MCP authorization URL opened in new tab. Complete the login there.');
      } else {
        setError('Failed to start MCP authorization');
      }
    } catch (err) {
      console.error('Start MCP auth error:', err);
      setError(err.response?.data?.error || 'Failed to start MCP authorization');
    } finally {
      setOauthLoading(false);
    }
  };

  const checkMcpStatus = async () => {
    try {
      const resp = await apiRequest('/api/mcp/status');
      if (resp.data?.authorized) {
        setSuccess('MCP is authorized');
      } else {
        setError('MCP is not authorized');
      }
    } catch (err) {
      console.error('Check MCP status error:', err);
      setError(err.response?.data?.error || 'Failed to check MCP status');
    }
  };

  const openDeleteDialog = (account) => {
    setDeletingAccount(account);
    setDeleteDialogOpen(true);
  };

  const openImportDialog = (account) => {
    setSelectedAccount(account);
    setImportDialogOpen(true);
  };

  // Group accounts by broker for display.
  const accountGroups = useMemo(() => {
    const groups = BROKER_SECTIONS.map(s => ({ ...s, accounts: [] }));
    accounts.forEach(acc => {
      const t = String(acc.apiType || acc.api_type || 'manual').toLowerCase();
      groups.find(s => s.match(t)).accounts.push(acc);
    });
    return groups.filter(g => g.accounts.length > 0);
  }, [accounts]);

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {rhLoginStatus && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {rhLoginStatus}
        </Alert>
      )}

      <Tabs value={topTab} onChange={(_, v) => setTopTab(v)} sx={{ mb: 2 }}>
        <Tab label="Accounts" />
        <Tab label={`Broker Connections${connections.length ? ` (${connections.length})` : ''}`} />
      </Tabs>

      {plaidLinkToken && (
        <PlaidLinkLauncher
          token={plaidLinkToken}
          onSuccess={(pt) => plaidRelinkConn ? handlePlaidRelinkSuccess() : handlePlaidSuccess(pt)}
          onExit={() => { setPlaidLinkToken(null); setPlaidRelinkConn(null); setPendingConnLabel(''); }}
        />
      )}

      {topTab === 1 && (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Broker Connections</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setConnDialogOpen(true)}
            >
              Add Connection
            </Button>
          </Box>
          {connections.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
              No broker connections yet. A connection holds broker credentials (e.g. a Robinhood
              bearer token or Tradier API key) and can be shared by multiple accounts.
            </Typography>
          ) : (
            <List dense>
              {connections.map((conn) => {
                const exp = conn.tokenExpiresAt ? new Date(conn.tokenExpiresAt) : null;
                const msLeft = exp ? exp - Date.now() : null;
                const daysLeft = msLeft !== null ? Math.floor(msLeft / 86400000) : null;
                const expired = msLeft !== null && msLeft <= 0;
                const expiring = msLeft !== null && !expired && msLeft < 2 * 86400000;
                const linked = accounts.filter(a => a.connectionId === conn.id);
                return (
                  <ListItem key={conn.id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{conn.label}</Typography>
                          <Chip label={conn.provider.toUpperCase()} size="small" color="primary" variant="outlined" />
                          {conn.status === 'needs_reauth' && <Chip label="NEEDS REAUTH" size="small" color="error" />}
                          {exp && (
                            <Chip
                              label={expired ? `Expired ${exp.toLocaleDateString()}` : `Expires ${exp.toLocaleDateString()}${daysLeft !== null ? ` (${daysLeft}d)` : ''}`}
                              size="small"
                              color={expired ? 'error' : (expiring ? 'warning' : 'default')}
                              variant={expired || expiring ? 'filled' : 'outlined'}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        linked.length
                          ? `Used by: ${linked.map(a => a.name).join(', ')}`
                          : 'Not attached to any account'
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {conn.provider === 'schwab' ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSchwabAuthorize(conn, linked[0]?.id)}
                            disabled={connBusy || !linked.length}
                            title={linked.length ? 'Start Schwab OAuth — tokens save to this connection' : 'Attach a Schwab account first'}
                          >
                            Authorize
                          </Button>
                        ) : conn.provider === 'plaid' ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handlePlaidRelink(conn)}
                            disabled={connBusy}
                            title="Re-authenticate this institution via Plaid Link (update mode)"
                          >
                            Re-link
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setConnTokenDialog(conn); setConnTokenInput(''); }}
                            disabled={connBusy}
                          >
                            Update Token
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDiscover(conn)}
                          disabled={connBusy}
                          title="Fetch broker-side accounts and attach them to this connection"
                        >
                          Discover
                        </Button>
                        <IconButton size="small" onClick={() => { setConnRenameDialog(conn); setConnRenameInput(conn.label); }} title="Rename" disabled={connBusy}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleTestConnectionRow(conn)} title="Test token" disabled={connBusy}>
                          <TestConnectionIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setConnDeleteTarget(conn)} title="Delete connection" disabled={connBusy}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
      )}

      {topTab === 0 && (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Accounts</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={() => navigate('/field-mappings')}
              >
                Field Mappings
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Add Account
              </Button>
            </Box>
          </Box>
          {loading && accounts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : accounts.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>
              No accounts found. Create your first account to get started.
            </Typography>
          ) : (
            <Box>
              {accountGroups.map((group) => (
                <Box key={group.key} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, px: 1 }}>
                    {group.logo ? (
                      <Box
                        component="img"
                        src={group.logo}
                        alt={group.label}
                        sx={{ height: 28, maxWidth: 120, objectFit: 'contain' }}
                      />
                    ) : (
                      <OtherIcon color="action" sx={{ fontSize: 28 }} />
                    )}
                    <Typography variant="h6">{group.label}</Typography>
                    <Chip label={group.accounts.length} size="small" variant="outlined" />
                  </Box>
                  <List>
                    {group.accounts.map((account, index) => (
                <React.Fragment key={account.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6">{account.name}</Typography>
                          {account.apiType && account.apiType !== 'manual' && (
                            <Chip
                              label={account.apiType.toUpperCase()}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {account.apiType === 'robinhood-api' && (
                            <Chip
                              label="NATIVE"
                              size="small"
                              color="secondary"
                              variant="outlined"
                              title="Using native JS Robinhood API (no Python)"
                            />
                          )}
                          {account.accountType && (
                            <Chip
                              label={account.accountType === 'taxable' ? 'Taxable' : 'Non-Taxable'}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: account.accountType === 'taxable' ? 'warning.main' : 'success.main',
                                borderColor: account.accountType === 'taxable' ? 'warning.main' : 'success.main'
                              }}
                            />
                          )}
                          {account.hasApiCredentials && account.syncEnabled && (
                            <Chip
                              label="Auto Sync"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {account.tradeCount || 0} trades
                          </Typography>
                          {account.lastSyncDate && (
                            <Typography variant="caption" color="textSecondary">
                              Last sync: {new Date(account.lastSyncDate).toLocaleDateString()}
                            </Typography>
                          )}
                          {account.apiType && account.apiType !== 'manual' && account.apiAccountId && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              Account ID: {account.apiAccountId}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {!['plaid', 'manual'].includes(String(account.apiType || '').toLowerCase()) && (
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleSetDefault(account)}
                            title={account.isDefault ? 'Default trading account (Quick Trade) — click to unset' : 'Set as default trading account (Quick Trade)'}
                            color={account.isDefault ? 'warning' : 'default'}
                          >
                            {account.isDefault ? <StarIcon /> : <StarBorderIcon />}
                          </IconButton>
                        )}
                        <Switch
                          checked={account.isActive !== false}
                          onChange={() => handleToggleActive(account)}
                          color="primary"
                          title={account.isActive !== false ? "Disable Account" : "Enable Account"}
                        />
                        {account.hasApiCredentials && (
                          <>
                            <IconButton
                              edge="end"
                              onClick={() => handleTestConnection(account)}
                              title="Test API Connection"
                              color="info"
                              disabled={account.isActive === false}
                            >
                              <TestConnectionIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleSyncAccount(account)}
                              title="Sync Data from API"
                              color="primary"
                              disabled={account.isActive === false}
                            >
                              <SyncIcon />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          edge="end"
                          onClick={(e) => openMoreMenu(e, account)}
                          title="More Actions"
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => openEditDialog(account)}
                          title="Edit Account"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => openDeleteDialog(account)}
                          title="Delete Account"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < group.accounts.length - 1 && <Divider />}
                </React.Fragment>
                    ))}
                  </List>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Account row overflow menu */}
      <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={closeMoreMenu}>
        {moreMenuAccount?.apiType === 'schwab' && (
          <MenuItem
            onClick={() => runMoreAction(handleRefreshToken)}
            disabled={moreMenuAccount.isActive === false}
          >
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            Refresh Access Token
          </MenuItem>
        )}
        {moreMenuAccount && (isRobinhoodApiType(moreMenuAccount.apiType) || moreMenuAccount.apiType === 'tradier-mcp') && (
          <MenuItem
            onClick={() => runMoreAction(moreMenuAccount.apiType === 'tradier-mcp' ? handleGetTradierMcpPositions : handleGetRobinhoodPositions)}
            disabled={moreMenuAccount.isActive === false}
          >
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            Refresh Positions
          </MenuItem>
        )}
        {moreMenuAccount?.hasApiCredentials && (
          <MenuItem onClick={() => runMoreAction(handleReconcileTrades)} disabled={loading}>
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            Reconcile Assignment Trades
          </MenuItem>
        )}
        <MenuItem onClick={() => runMoreAction(openImportDialog)}>
          <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
          Import Data
        </MenuItem>
        <MenuItem onClick={() => runMoreAction(fetchImportHistory)}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          View History
        </MenuItem>
      </Menu>

      {/* Add Account Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Account</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Account Name"
              fullWidth
              variant="outlined"
              value={newAccountName}
              onChange={(e) => setNewAccountName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>API Integration Type</InputLabel>
              <Select
                value={newApiType}
                onChange={(e) => setNewApiType(e.target.value)}
                label="API Integration Type"
              >
                <MenuItem value="manual">Manual Entry Only</MenuItem>
                <MenuItem value="tradier">Tradier API</MenuItem>
                <MenuItem value="schwab">Schwab API</MenuItem>
                <MenuItem value="robinhood">Robinhood (robin_stocks)</MenuItem>
                <MenuItem value="robinhood-api">Robinhood API (native JS)</MenuItem>
                <MenuItem value="robinhood-mcp">Robinhood MCP Integration</MenuItem>
                <MenuItem value="tradier-mcp">Tradier MCP Integration</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={newAccountType}
                onChange={(e) => setNewAccountType(e.target.value)}
                label="Account Type"
              >
                <MenuItem value="taxable">Taxable</MenuItem>
                <MenuItem value="non-taxable">Non-Taxable</MenuItem>
              </Select>
            </FormControl>

            {connectionsForType(newApiType).length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Broker Connection</InputLabel>
                <Select
                  value={newConnectionId}
                  onChange={(e) => setNewConnectionId(e.target.value)}
                  label="Broker Connection"
                >
                  <MenuItem value=""><em>None — enter credentials below</em></MenuItem>
                  {connectionsForType(newApiType).map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                  When a connection is selected, credentials are shared from it — fields below are ignored.
                </Typography>
              </FormControl>
            )}

            {newApiType === 'tradier' && !newConnectionId && (
              <>
                <TextField
                  margin="dense"
                  label="API Token"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={newApiToken}
                  onChange={(e) => setNewApiToken(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Your API authentication token"
                />

                <TextField
                  margin="dense"
                  label="API Account ID"
                  fullWidth
                  variant="outlined"
                  value={newApiAccountId}
                  onChange={(e) => setNewApiAccountId(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Your account ID from the API provider"
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={newEnvironment}
                    onChange={(e) => setNewEnvironment(e.target.value)}
                    label="Environment"
                  >
                    <MenuItem value="production">Production</MenuItem>
                    <MenuItem value="sandbox">Sandbox/Testing</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Sync Frequency</InputLabel>
                  <Select
                    value={newSyncFrequency}
                    onChange={(e) => setNewSyncFrequency(e.target.value)}
                    label="Sync Frequency"
                  >
                    <MenuItem value="manual">Manual Only</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    id="newSyncEnabled"
                    checked={newSyncEnabled}
                    onChange={(e) => setNewSyncEnabled(e.target.checked)}
                  />
                  <label htmlFor="newSyncEnabled">
                    Enable automatic sync
                  </label>
                </Box>
              </>
            )}

            {newApiType === 'schwab' && !newConnectionId && (
              <>
                <TextField
                  margin="dense"
                  label="Schwab App Key"
                  fullWidth
                  variant="outlined"
                  value={newAppKey}
                  onChange={(e) => setNewAppKey(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Schwab App Secret"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={newAppSecret}
                  onChange={(e) => setNewAppSecret(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={newEnvironment}
                    onChange={(e) => setNewEnvironment(e.target.value)}
                    label="Environment"
                  >
                    <MenuItem value="production">Production</MenuItem>
                    <MenuItem value="sandbox">Sandbox/Testing</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Sync Frequency</InputLabel>
                  <Select
                    value={newSyncFrequency}
                    onChange={(e) => setNewSyncFrequency(e.target.value)}
                    label="Sync Frequency"
                  >
                    <MenuItem value="manual">Manual Only</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    id="newSyncEnabledSchwab"
                    checked={newSyncEnabled}
                    onChange={(e) => setNewSyncEnabled(e.target.checked)}
                  />
                  <label htmlFor="newSyncEnabledSchwab">
                    Enable automatic sync
                  </label>
                </Box>
              </>
            )}

            {isRobinhoodApiType(newApiType) && newConnectionId && (
              <TextField
                margin="dense"
                label="Robinhood Account Number (optional)"
                fullWidth
                variant="outlined"
                value={newApiAccountId}
                onChange={(e) => setNewApiAccountId(e.target.value)}
                sx={{ mb: 1 }}
                helperText="Sub-account number — required when multiple accounts share one Robinhood connection, otherwise positions/orders are duplicated"
              />
            )}

            {isRobinhoodApiType(newApiType) && !newConnectionId && (
              <>
                <TextField
                  margin="dense"
                  label="Robinhood Username"
                  fullWidth
                  variant="outlined"
                  value={newRobinUsername}
                  onChange={(e) => setNewRobinUsername(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Robinhood Password"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={newRobinPassword}
                  onChange={(e) => setNewRobinPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Device Token (optional)"
                  fullWidth
                  variant="outlined"
                  value={newRobinDeviceToken}
                  onChange={(e) => setNewRobinDeviceToken(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Robinhood device token for fewer MFA prompts"
                />
                <TextField
                  margin="dense"
                  label="MFA Code (optional)"
                  fullWidth
                  variant="outlined"
                  value={newRobinMfa}
                  onChange={(e) => setNewRobinMfa(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Provide current MFA code if prompted"
                />
                <TextField
                  margin="dense"
                  label="Robinhood Account Number (optional)"
                  fullWidth
                  variant="outlined"
                  value={newApiAccountId}
                  onChange={(e) => setNewApiAccountId(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Sub-account number — set after creation via Edit if multiple accounts share one login"
                />
              </>
            )}
            {newApiType === 'robinhood-mcp' && (
              <>
                <TextField
                  margin="dense"
                  label="Account Number"
                  fullWidth
                  variant="outlined"
                  value={newApiAccountId}
                  onChange={(e) => setNewApiAccountId(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Robinhood account number (e.g., 123456789)"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                  When using Robinhood MCP, positions will be fetched using the Robinhood MCP server. CSV import will still be available as a fallback.
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAccount} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Account Name"
              fullWidth
              variant="outlined"
              value={editAccountName}
              onChange={(e) => setEditAccountName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>API Integration Type</InputLabel>
              <Select
                value={editApiType}
                onChange={(e) => setEditApiType(e.target.value)}
                label="API Integration Type"
              >
                <MenuItem value="manual">Manual Entry Only</MenuItem>
                <MenuItem value="tradier">Tradier API</MenuItem>
                <MenuItem value="schwab">Schwab API</MenuItem>
                <MenuItem value="robinhood">Robinhood (robin_stocks)</MenuItem>
                <MenuItem value="robinhood-api">Robinhood API (native JS)</MenuItem>
                <MenuItem value="robinhood-mcp">Robinhood MCP Integration</MenuItem>
                <MenuItem value="tradier-mcp">Tradier MCP Integration</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={editAccountType}
                onChange={(e) => setEditAccountType(e.target.value)}
                label="Account Type"
              >
                <MenuItem value="taxable">Taxable</MenuItem>
                <MenuItem value="non-taxable">Non-Taxable</MenuItem>
              </Select>
            </FormControl>

            {connectionsForType(editApiType).length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Broker Connection</InputLabel>
                <Select
                  value={editConnectionId}
                  onChange={(e) => setEditConnectionId(e.target.value)}
                  label="Broker Connection"
                >
                  <MenuItem value=""><em>None — use credentials stored on this account</em></MenuItem>
                  {connectionsForType(editApiType).map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                  ))}
                </Select>
                {editConnectionId && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                    Credentials come from the connection — fields below are ignored.
                  </Typography>
                )}
              </FormControl>
            )}

            {editApiType === 'tradier' && !editConnectionId && (
              <>
                <TextField
                  margin="dense"
                  label="API Token"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={editApiToken}
                  onChange={(e) => setEditApiToken(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Your API authentication token"
                />

                <TextField
                  margin="dense"
                  label="API Account ID"
                  fullWidth
                  variant="outlined"
                  value={editApiAccountId}
                  onChange={(e) => setEditApiAccountId(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Your account ID from the API provider"
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={editEnvironment}
                    onChange={(e) => setEditEnvironment(e.target.value)}
                    label="Environment"
                  >
                    <MenuItem value="production">Production</MenuItem>
                    <MenuItem value="sandbox">Sandbox/Testing</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Sync Frequency</InputLabel>
                  <Select
                    value={editSyncFrequency}
                    onChange={(e) => setEditSyncFrequency(e.target.value)}
                    label="Sync Frequency"
                  >
                    <MenuItem value="manual">Manual Only</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    id="editSyncEnabled"
                    checked={editSyncEnabled}
                    onChange={(e) => setEditSyncEnabled(e.target.checked)}
                  />
                  <label htmlFor="editSyncEnabled">
                    Enable automatic sync
                  </label>
                </Box>
              </>
            )}

            {editApiType === 'schwab' && (
              <>
                <TextField
                  margin="dense"
                  label="Schwab App Key"
                  fullWidth
                  variant="outlined"
                  value={editAppKey}
                  onChange={(e) => setEditAppKey(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Schwab App Secret"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={editAppSecret}
                  onChange={(e) => setEditAppSecret(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={editEnvironment}
                    onChange={(e) => setEditEnvironment(e.target.value)}
                    label="Environment"
                  >
                    <MenuItem value="production">Production</MenuItem>
                    <MenuItem value="sandbox">Sandbox/Testing</MenuItem>
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Schwab OAuth</Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSchwabConnect}
                  disabled={oauthLoading || !editingAccount}
                  sx={{ mb: 2 }}
                >
                  {oauthLoading ? <CircularProgress size={18} /> : 'Authorize with Schwab'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Redirects to Schwab and back automatically — requires the app's callback URL registered in your Schwab developer portal.
                </Typography>
                <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">manual (advanced)</Typography></Divider>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Redirect URI</InputLabel>
                  <Select
                    value={oauthRedirectUri}
                    onChange={(e) => setOauthRedirectUri(e.target.value)}
                    label="Redirect URI"
                  >
                    <MenuItem value="https://mytradeshub.net/api/schwab-api/oauth/callback">
                      https://mytradeshub.net/api/schwab-api/oauth/callback
                    </MenuItem>
                    <MenuItem value="https://127.0.0.1">
                      https://127.0.0.1
                    </MenuItem>
                  </Select>
                  <FormHelperText>Must match the URI registered in Schwab app settings</FormHelperText>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={generateSchwabAuthUrl}
                    disabled={oauthLoading || !editingAccount}
                  >
                    {oauthLoading ? <CircularProgress size={18} /> : 'Generate Auth URL'}
                  </Button>
                  {oauthAuthUrl && (
                    <Button
                      variant="text"
                      onClick={() => window.open(oauthAuthUrl, '_blank')}
                      disabled={oauthLoading}
                    >
                      Open Auth URL
                    </Button>
                  )}
                </Box>
                <TextField
                  label="Authorization Code or Full Redirect URL"
                  fullWidth
                  variant="outlined"
                  value={oauthCode}
                  onChange={(e) => setOauthCode(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Paste the code from Schwab or the entire redirect URL"
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={exchangeSchwabCode}
                    disabled={oauthLoading || !editingAccount}
                  >
                    {oauthLoading ? <CircularProgress size={18} /> : 'Exchange Code'}
                  </Button>
                </Box>
              </>
            )}

            {isRobinhoodApiType(editApiType) && editConnectionId && (
              <TextField
                margin="dense"
                label="Robinhood Account Number (optional)"
                fullWidth
                variant="outlined"
                value={editApiAccountId}
                onChange={(e) => setEditApiAccountId(e.target.value)}
                sx={{ mb: 1 }}
                helperText="Sub-account number — required when multiple accounts share one Robinhood connection, otherwise positions/orders are duplicated"
              />
            )}

            {isRobinhoodApiType(editApiType) && !editConnectionId && (
              <>
                <TextField
                  margin="dense"
                  label="Robinhood Username"
                  fullWidth
                  variant="outlined"
                  value={editRobinUsername}
                  onChange={(e) => setEditRobinUsername(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Robinhood Password"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={editRobinPassword}
                  onChange={(e) => setEditRobinPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Device Token (optional)"
                  fullWidth
                  variant="outlined"
                  value={editRobinDeviceToken}
                  onChange={(e) => setEditRobinDeviceToken(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Robinhood device token for fewer MFA prompts"
                />
                <TextField
                  margin="dense"
                  label="MFA Code (optional)"
                  fullWidth
                  variant="outlined"
                  value={editRobinMfa}
                  onChange={(e) => setEditRobinMfa(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Provide current MFA code if prompted"
                />
                <TextField
                  margin="dense"
                  label="Robinhood Account Number (optional)"
                  fullWidth
                  variant="outlined"
                  value={editApiAccountId}
                  onChange={(e) => setEditApiAccountId(e.target.value)}
                  sx={{ mb: 1 }}
                  helperText="Sub-account number — required when multiple accounts share one Robinhood login, otherwise positions/orders are duplicated"
                />
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={discoverRobinhoodAccounts}
                    disabled={subAcctsLoading}
                  >
                    {subAcctsLoading ? <CircularProgress size={16} /> : 'Fetch account numbers'}
                  </Button>
                  {editSubAccounts.length > 0 && (
                    <Select
                      size="small"
                      displayEmpty
                      value=""
                      onChange={(e) => setEditApiAccountId(e.target.value)}
                      sx={{ flex: 1 }}
                    >
                      <MenuItem value="" disabled>Pick a sub-account…</MenuItem>
                      {editSubAccounts.map((a) => (
                        <MenuItem key={a.account_number} value={a.account_number}>
                          {a.account_number} — cash ${Number(a.cash || 0).toLocaleString()}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Offline session import</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  When this server is rate-limited by Robinhood, run{' '}
                  <code>node server/scripts/robinhood_login.js</code> from an
                  unblocked network and paste the JSON it prints. The token is
                  validated against Robinhood before being stored.
                </Typography>
                <TextField
                  margin="dense"
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  label="Session JSON (optional)"
                  placeholder='{"access_token":"…","refresh_token":"…","device_token":"…"}'
                  value={rhImportText}
                  onChange={(e) => setRhImportText(e.target.value)}
                  sx={{ mb: 1 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleImportSession}
                  disabled={!rhImportText.trim()}
                >
                  Import &amp; Validate Session
                </Button>
              </>
            )}
            {editApiType === 'robinhood-mcp' && (
              <>
                <TextField
                  margin="dense"
                  label="Account Number"
                  fullWidth
                  variant="outlined"
                  value={editApiAccountId}
                  onChange={(e) => setEditApiAccountId(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Robinhood account number (e.g., 123456789)"
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Robinhood MCP OAuth</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Authenticate with Robinhood MCP to enable API access. This is required for syncing positions and trades.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={startMcpAuth}
                    disabled={oauthLoading}
                  >
                    {oauthLoading ? <CircularProgress size={18} /> : 'Start OAuth Login'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={checkMcpStatus}
                    disabled={oauthLoading}
                  >
                    Check Status
                  </Button>
                </Box>
              </>
            )}
            {editApiType === 'tradier-mcp' && (
              <>
                <TextField
                  margin="dense"
                  label="API Token"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={editApiToken}
                  onChange={(e) => setEditApiToken(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Your Tradier API token from https://web.tradier.com/user/api"
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Trading Mode</InputLabel>
                  <Select
                    value={editEnvironment}
                    label="Trading Mode"
                    onChange={(e) => setEditEnvironment(e.target.value)}
                  >
                    <MenuItem value="production">Live Trading</MenuItem>
                    <MenuItem value="paper">Paper Trading</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditAccount} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Account or Clear Data</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            What would you like to do with the account "{deletingAccount?.name}"?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleClearAccountData}
              disabled={loading}
              sx={{ justifyContent: 'flex-start', p: 2 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2">Clear Trade Data Only</Typography>
                <Typography variant="body2" color="textSecondary">
                  Remove all trades but keep the account and its settings
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteAccount}
              disabled={loading}
              sx={{ justifyContent: 'flex-start', p: 2 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2">Delete Entire Account</Typography>
                <Typography variant="body2" color="textSecondary">
                  Permanently delete the account and all associated data
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Data for {selectedAccount?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Format</InputLabel>
              <Select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="schwab">Schwab</MenuItem>
                <MenuItem value="robinhood">Robinhood</MenuItem>
                <MenuItem value="tradier">Tradier</MenuItem>
                <MenuItem value="pershing-holdings">Pershing Holdings</MenuItem>
                <MenuItem value="pershing-history">Pershing History</MenuItem>
                <MenuItem value="pershing-transactions">Pershing Transactions (Full)</MenuItem>
              </Select>
            </FormControl>

            <input
              id="file-input"
              type="file"
              accept={selectedFormat === 'pershing-holdings' || selectedFormat === 'pershing-history' ? '.xlsx' : '.csv'}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input">
              <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                {file ? `Selected: ${file.name}` : 'Select File'}
              </Button>
            </label>

            {previewData.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  File Preview (first 5 rows):
                </Typography>
                <Box sx={{ overflow: 'auto', maxHeight: 200, border: 1, borderColor: 'divider', p: 1 }}>
                  <pre style={{ fontSize: '12px', margin: 0 }}>
                    {JSON.stringify(previewData, null, 2)}
                  </pre>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!file || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Import History for {selectedAccount?.name}</DialogTitle>
        <DialogContent>
          {!importHistory || importHistory.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>
              No import history found for this account.
            </Typography>
          ) : (
            <List>
              {importHistory.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={`Import Batch: ${item.importBatch}`}
                      secondary={`Date: ${new Date(item.importDate).toLocaleDateString()} | Records: ${item.recordCount}`}
                    />
                  </ListItem>
                  {index < importHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sync Account Data - {syncingAccount?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Pull historic data from API by specifying a date range. Leave dates empty to sync all available data.
              {syncingAccount?.lastSyncDate && (
                <><br />Start date has been pre-filled with the day after your last sync ({new Date(syncingAccount.lastSyncDate).toLocaleDateString()}) for incremental updates.</>
              )}
            </Typography>

            <TextField
              label="Start Date"
              type="date"
              value={syncStartDate}
              onChange={(e) => setSyncStartDate(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Optional: Earliest date to sync data from"
            />

            <TextField
              label="End Date"
              type="date"
              value={syncEndDate}
              onChange={(e) => setSyncEndDate(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Optional: Latest date to sync data to"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button
            onClick={performSync}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Syncing...' : 'Sync Data'}
          </Button>
        </DialogActions>
        {loading && syncProgress && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              {syncProgress}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Elapsed time: {Math.floor(syncElapsedTime / 60)}m {syncElapsedTime % 60}s
            </Typography>
          </Box>
        )}
      </Dialog>

      {/* Sync Details Dialog (Raw API Response) */}
      <Dialog
        open={syncDetailsDialogOpen}
        onClose={() => setSyncDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Sync Details - Raw API Response Sample
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            The following data was received from the brokerage API. This shows a sample of the first few transactions.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" display="block">Total Fetched</Typography>
                <Typography variant="body2" fontWeight="bold">{rawSyncData?.totalFetched || 0}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" display="block">Imported</Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">{rawSyncData?.importedCount || 0}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" display="block">Duplicates</Typography>
                <Typography variant="body2" fontWeight="bold" color="warning.main">{rawSyncData?.duplicateCount || 0}</Typography>
              </Grid>
            </Grid>
          </Box>

          {rawSyncData?.apiRequests && (
            <Box sx={{ mb: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb' }}>
              <Typography variant="caption" fontWeight="bold" color="primary" display="block" sx={{ mb: 0.5 }}>
                API Requests Made:
              </Typography>
              {rawSyncData.apiRequests.map((req, idx) => (
                <Box key={idx} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                  <Typography variant="caption" fontWeight="bold" display="block">
                    {req.name}
                  </Typography>
                  <Typography variant="caption" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', color: '#555' }}>
                    {req.url}
                  </Typography>
                  {req.params && (
                    <pre style={{ margin: '4px 0 0 0', fontSize: '0.7rem', color: '#666' }}>
                      {JSON.stringify(req.params, null, 2)}
                    </pre>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {rawSyncData?.breakdown && (
            <Box sx={{ mb: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>Sync Breakdown</Typography>
              <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                {JSON.stringify(rawSyncData.breakdown, null, 2)}
              </pre>
            </Box>
          )}

          <Typography variant="caption" fontWeight="bold" sx={{ mt: 2, display: 'block' }}>
            Raw Transaction Data (JSON):
          </Typography>
          <Box
            sx={{
              mt: 1,
              p: 2,
              bgcolor: '#1e1e1e',
              color: '#d4d4d4',
              borderRadius: 1,
              maxHeight: '400px',
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.8rem'
            }}
          >
            <pre style={{ margin: 0 }}>
              {JSON.stringify(rawSyncData?.rawResponseSample, null, 2)}
            </pre>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Broker Connection Dialog */}
      <Dialog open={connDialogOpen} onClose={() => setConnDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Broker Connection</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Provider</InputLabel>
              <Select
                value={connProvider}
                onChange={(e) => { setConnProvider(e.target.value); setConnToken(''); }}
                label="Provider"
              >
                <MenuItem value="robinhood">Robinhood</MenuItem>
                <MenuItem value="schwab">Schwab</MenuItem>
                <MenuItem value="tradier">Tradier</MenuItem>
                <MenuItem value="plaid">Plaid (read-only aggregation)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Label"
              fullWidth
              variant="outlined"
              value={connLabel}
              onChange={(e) => setConnLabel(e.target.value)}
              sx={{ mb: 2 }}
              helperText="A friendly name, e.g. 'Robinhood — main login'"
            />
            {connProvider === 'plaid' ? (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Plaid connections are created through Plaid Link — you'll log into the
                institution and every account under that login is discovered and attached
                automatically. The label you enter above is used for the connection.
              </Typography>
            ) : connProvider === 'schwab' ? (
              <>
                <TextField
                  margin="dense"
                  label="Schwab App Key"
                  fullWidth
                  variant="outlined"
                  value={connAppKey}
                  onChange={(e) => setConnAppKey(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Schwab App Secret"
                  fullWidth
                  variant="outlined"
                  type="password"
                  value={connAppSecret}
                  onChange={(e) => setConnAppSecret(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="OAuth authorization happens on the attached account's edit dialog"
                />
              </>
            ) : (
              <>
                <TextField
                  margin="dense"
                  label={connProvider === 'robinhood' ? 'Bearer Token' : 'API Token / Key'}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={3}
                  value={connToken}
                  onChange={(e) => setConnToken(e.target.value)}
                  sx={{ mb: 1 }}
                  helperText={
                    connProvider === 'robinhood'
                      ? "Paste the Bearer token from robinhood.com (DevTools → any api.robinhood.com request → Authorization header), or the full web:auth_state JSON blob"
                      : 'The API token for this broker login'
                  }
                />
                {connProvider === 'robinhood' && <RobinhoodTokenHelp />}
                {(() => {
                  const t = parseTokenInput(connToken).access_token;
                  const exp = t && decodeJwtExp(t);
                  return exp ? (
                    <Typography variant="caption" color={exp < new Date() ? 'error' : 'textSecondary'} sx={{ display: 'block', mb: 1 }}>
                      Token {exp < new Date() ? 'expired' : 'expires'} {exp.toLocaleString()}
                    </Typography>
                  ) : null;
                })()}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnDialogOpen(false)}>Cancel</Button>
          {connProvider === 'plaid' ? (
            <Button
              variant="contained"
              onClick={() => { setConnDialogOpen(false); setPendingConnLabel(connLabel.trim()); handleConnectPlaid(); }}
              disabled={plaidBusy}
            >
              {plaidBusy ? <CircularProgress size={18} /> : 'Launch Plaid Link'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleCreateConnection} disabled={connBusy}>
              {connBusy ? <CircularProgress size={18} /> : 'Create & Validate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Update Connection Token Dialog */}
      <Dialog open={!!connTokenDialog} onClose={() => setConnTokenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Token — {connTokenDialog?.label}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {connTokenDialog?.provider === 'robinhood'
                ? 'Paste the web:auth_state blob or a fresh Bearer token from robinhood.com. Applies to all accounts attached to this connection.'
                : 'Paste the new API token / app key for this connection.'}
            </Typography>
            {connTokenDialog?.provider === 'robinhood' && <RobinhoodTokenHelp />}
            <TextField
              margin="dense"
              label="Token"
              fullWidth
              variant="outlined"
              multiline
              minRows={3}
              value={connTokenInput}
              onChange={(e) => setConnTokenInput(e.target.value)}
              sx={{ mb: 1 }}
            />
            {(() => {
              const t = parseTokenInput(connTokenInput).access_token;
              const exp = t && decodeJwtExp(t);
              return exp ? (
                <Typography variant="caption" color={exp < new Date() ? 'error' : 'textSecondary'} sx={{ display: 'block' }}>
                  Token {exp < new Date() ? 'expired' : 'expires'} {exp.toLocaleString()}
                </Typography>
              ) : null;
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnTokenDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateConnectionToken} disabled={connBusy}>
            {connBusy ? <CircularProgress size={18} /> : 'Validate & Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Connection Dialog */}
      <Dialog open={!!connRenameDialog} onClose={() => setConnRenameDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Connection</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Label"
            fullWidth
            variant="outlined"
            value={connRenameInput}
            onChange={(e) => setConnRenameInput(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnRenameDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRenameConnection} disabled={connBusy}>
            {connBusy ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Connection Confirm */}
      <Dialog open={!!connDeleteTarget} onClose={() => setConnDeleteTarget(null)}>
        <DialogTitle>Delete Connection</DialogTitle>
        <DialogContent>
          <Typography>
            Delete "{connDeleteTarget?.label}"? Accounts attached to it will keep working only if they
            have their own stored credentials.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConnection} disabled={connBusy}>
            {connBusy ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Accounts;